'use memo';

import { useContext, useEffect, useState } from 'react';
import {
  AgxButton,
  AgxBrowserFilePdfEditor,
  AgxDivider,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  generateEditedPdf,
  useToast,
} from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';
import { DocumentTypes, UploadedDocument } from '@urbanx/agx.campaigns.client';
import type {
  BrowserLoadedFile,
  PdfEditorBrowserFileSource,
  PdfEditorState,
} from '@urbanx/agx.ui-components2';
import { uploadFile } from 'services';
import { useSelectedCampaign } from 'hooks';
import {
  buildComplianceState,
  buildContractFileName,
  ComplianceData,
  ComplianceKey,
  getComplianceItems,
  getFinalEnabled,
  getRequiredCompletion,
} from './contractCompliance';
import {
  contractUploadFieldBlockTemplates,
  initialContractUploadEditState,
} from './contractUploadPdfTemplate';
import ContractComplianceStep from './ContractComplianceStep';
import ContractUploadStep from './ContractUploadStep';
import ContractVersionStep from './ContractVersionStep';
import './ContractUploadModal.scss';

type ContractUploadModalStep = 'upload' | 'compliance';

interface ContractUploadModalProps {
  isOpen: boolean;
  showTenancyAgreement: boolean;
  onClose: () => void;
  onComplete: (payload: {
    complianceData: ComplianceData;
    uploadedDocument: UploadedDocument;
    isFinalSelected: boolean;
  }) => Promise<void>;
}

const buildRenamedFormData = (file: File, name: string) => {
  const formData = new FormData();
  formData.append('File', file, name);
  return formData;
};

const ContractUploadModal = ({
  isOpen,
  showTenancyAgreement,
  onClose,
  onComplete,
}: ContractUploadModalProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const { campaign: selectedCampaign } = useSelectedCampaign();
  const [loadedFile, setLoadedFile] = useState<BrowserLoadedFile | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreparingReview, setIsPreparingReview] = useState(false);
  const [reviewPdfSource, setReviewPdfSource] = useState<PdfEditorBrowserFileSource | undefined>(
    undefined
  );
  const [complianceData, setComplianceData] = useState<ComplianceData>(() =>
    buildComplianceState()
  );
  const [contractEditState, setContractEditState] = useState<PdfEditorState>(
    initialContractUploadEditState
  );
  const [editableEditorInitialState, setEditableEditorInitialState] = useState<PdfEditorState>(
    initialContractUploadEditState
  );
  const [isFinalSelected, setIsFinalSelected] = useState(false);
  const [currentStep, setCurrentStep] = useState<ContractUploadModalStep>('upload');

  const { required, optional } = getComplianceItems(showTenancyAgreement);
  const requiredComplete = getRequiredCompletion(complianceData);
  const finalEnabled = getFinalEnabled(complianceData, showTenancyAgreement);
  const fileName = buildContractFileName(
    selectedCampaign?.address?.streetAddress,
    selectedCampaign?.address?.suburb
  );
  const filePath = selectedCampaign?.campaignId
    ? `${selectedCampaign.campaignId}/ListingDocuments/${fileName}`
    : undefined;

  useEffect(() => {
    if (!finalEnabled && isFinalSelected) {
      setIsFinalSelected(false);
    }
  }, [finalEnabled, isFinalSelected]);

  useEffect(() => {
    if (!isOpen) return;
    setLoadedFile(undefined);
    setIsSubmitting(false);
    setIsPreparingReview(false);
    setReviewPdfSource(undefined);
    setComplianceData(buildComplianceState());
    setContractEditState(initialContractUploadEditState);
    setEditableEditorInitialState(initialContractUploadEditState);
    setIsFinalSelected(false);
    setCurrentStep('upload');
  }, [isOpen]);

  useEffect(
    () => () => {
      if (loadedFile?.objectUrl) {
        URL.revokeObjectURL(loadedFile.objectUrl);
      }
    },
    [loadedFile]
  );

  const handleFileLoaded = (nextLoadedFile: BrowserLoadedFile) => {
    setLoadedFile(nextLoadedFile);
    setIsPreparingReview(false);
    setReviewPdfSource(undefined);
    setComplianceData(buildComplianceState());
    setContractEditState(initialContractUploadEditState);
    setEditableEditorInitialState(initialContractUploadEditState);
    setIsFinalSelected(false);
    setCurrentStep('upload');
  };

  const resetLoadedContract = () => {
    setLoadedFile(undefined);
    setIsPreparingReview(false);
    setReviewPdfSource(undefined);
    setComplianceData(buildComplianceState());
    setContractEditState(initialContractUploadEditState);
    setEditableEditorInitialState(initialContractUploadEditState);
    setIsFinalSelected(false);
    setCurrentStep('upload');
  };

  const handleComplianceToggle = (key: ComplianceKey, checked: boolean) => {
    setComplianceData((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const uploadLoadedContract = async () => {
    if (!loadedFile || !selectedCampaign?.campaignId) return undefined;

    const authToken = await getAuthToken();
    if (!authToken) return undefined;

    const editedPdf = await generateEditedContractPdf();
    if (!editedPdf) return undefined;

    const pdfBytes = new Uint8Array(editedPdf);
    const editedContractFile = new File([pdfBytes.buffer], fileName, {
      type: 'application/pdf',
      lastModified: loadedFile.file.lastModified,
    });
    const renamedFormData = buildRenamedFormData(editedContractFile, fileName);
    const uploadAbortController = new AbortController();
    const uploadTimeout = window.setTimeout(() => uploadAbortController.abort(), 60_000);

    try {
      return (await uploadFile(
        authToken,
        renamedFormData,
        selectedCampaign.campaignId,
        DocumentTypes.BlankSalesContract,
        null,
        uploadAbortController.signal,
        filePath
      )) as UploadedDocument | undefined;
    } finally {
      window.clearTimeout(uploadTimeout);
    }
  };

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      resetLoadedContract();
      onClose();
    }
  };

  const generateEditedContractPdf = async ({
    renderFieldBlockChrome = false,
  }: {
    renderFieldBlockChrome?: boolean;
  } = {}) => {
    if (!loadedFile) return undefined;

    return generateEditedPdf({
      pdfBytes: loadedFile.bytes,
      editState: contractEditState,
      fieldBlockTemplates: contractUploadFieldBlockTemplates,
      renderFieldBlockChrome,
    });
  };

  const handleMappingConfirmed = async () => {
    if (!loadedFile) return;

    setIsPreparingReview(true);
    try {
      const editedPdf = await generateEditedContractPdf();
      if (!editedPdf) return;

      setReviewPdfSource({
        bytes: editedPdf,
        fileName: loadedFile.fileName,
        documentKey: `${loadedFile.id}-review-${Date.now()}`,
      });
      setCurrentStep('compliance');
    } catch {
      pushToast({
        type: 'error',
        message: 'Error preparing contract preview',
      });
    } finally {
      setIsPreparingReview(false);
    }
  };

  const handleBackToMapping = () => {
    setEditableEditorInitialState(contractEditState);
    setReviewPdfSource(undefined);
    setCurrentStep('upload');
  };

  const handleComplete = async () => {
    if (!loadedFile || !requiredComplete) return;
    setIsSubmitting(true);
    try {
      const uploadedDocument = await uploadLoadedContract();

      if (!uploadedDocument) {
        pushToast({
          type: 'error',
          message: 'Error uploading contract',
        });
        return;
      }

      await onComplete({
        complianceData,
        uploadedDocument,
        isFinalSelected,
      });
      resetLoadedContract();
      onClose();
    } catch {
      pushToast({
        type: 'error',
        message: 'Error saving contract',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canComplete =
    Boolean(loadedFile) &&
    Boolean(reviewPdfSource) &&
    requiredComplete &&
    !isSubmitting &&
    (finalEnabled || !isFinalSelected);
  const controlsDisabled = !loadedFile || isSubmitting || isPreparingReview;
  const isUploadStep = currentStep === 'upload';

  return (
    <AgxModal
      type="fullHeight"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      minWidth="95vw"
      maxWidth="95vw"
      secondaryTitle={
        <AgxSpacer
          direction="vertical"
          gap="spacing-2xl"
          fill
          contentAlignment="start"
          padding={{ bottom: 'spacing-xl' }}
        >
          <AgxTypography
            id="contractUploadTitle"
            type="body_ui_strong"
            size="medium"
            color="var(--text-primary)"
          >
            {isUploadStep ? 'Step 1: Upload contract' : 'Step 2: Compliance Check'}
          </AgxTypography>
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          data-testid="modal-contractUpload"
          className="contract-upload-modal__content"
          direction="vertical"
          gap="spacing-xl"
          fill
          padding={{
            top: 'spacing-3xl',
            bottom: 'spacing-3xl',
            left: 'spacing-5xl',
            right: 'spacing-5xl',
          }}
        >
          {isUploadStep ? (
            <div className="contract-upload-modal__mapping-step">
              {!loadedFile && (
                <div className="contract-upload-modal__dropzone">
                  <ContractUploadStep
                    loadedFile={loadedFile}
                    disabled={isSubmitting}
                    onFileLoaded={handleFileLoaded}
                    onFileCleared={resetLoadedContract}
                  />
                </div>
              )}
              {loadedFile ? (
                <div className="contract-upload-modal__pdf-editor">
                  <AgxBrowserFilePdfEditor
                    id="contractUploadPdfPreview"
                    pdfFileName={loadedFile.fileName}
                    source={loadedFile}
                    showFieldBlockTools={false}
                    fieldBlockTemplates={contractUploadFieldBlockTemplates}
                    initialEditState={editableEditorInitialState}
                    onChange={setContractEditState}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="contract-upload-modal__compliance-step">
              <div className="contract-upload-modal__pdf-review">
                {reviewPdfSource ? (
                  <AgxBrowserFilePdfEditor
                    id="contractUploadPdfReview"
                    readOnly
                    pdfFileName={reviewPdfSource.fileName}
                    source={reviewPdfSource}
                    showToolbar={false}
                    showEditOverlays={false}
                  />
                ) : null}
              </div>
              <AgxSpacer
                className="contract-upload-modal__review-controls"
                direction="vertical"
                gap="spacing-3xl"
                fill
              >
                <ContractComplianceStep
                  required={required}
                  optional={optional}
                  complianceData={complianceData}
                  disabled={controlsDisabled}
                  onToggle={handleComplianceToggle}
                />
                <AgxDivider variant="horizontal" />
                <AgxSpacer direction="vertical" gap="spacing-lg" fill>
                  <AgxTypography
                    id="contractVersionTitle"
                    type="body_ui_strong"
                    size="small"
                    color="var(--text-tertiary)"
                  >
                    Contract version is
                  </AgxTypography>
                  <ContractVersionStep
                    finalEnabled={finalEnabled}
                    isFinalSelected={isFinalSelected}
                    disabled={controlsDisabled}
                    onSelectFinal={setIsFinalSelected}
                  />
                </AgxSpacer>
              </AgxSpacer>
            </div>
          )}
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          fill
          contentAlignment="center"
          contentJustification={isUploadStep ? 'center' : 'end'}
        >
          {isUploadStep ? (
            <AgxButton
              id="contractMappingConfirmed"
              dataTestId="btn-contractMappingConfirmed"
              text={isPreparingReview ? 'Preparing preview...' : 'All fields are mapped correctly'}
              variant="primary"
              size="large"
              onClick={handleMappingConfirmed}
              disabled={!loadedFile || isSubmitting || isPreparingReview}
            />
          ) : (
            <>
              <AgxButton
                id="contractReviewBack"
                text="Back"
                variant="hollow"
                size="large"
                onClick={handleBackToMapping}
                disabled={isSubmitting}
              />
              <AgxButton
                id="contractReviewComplete"
                dataTestId="btn-contractComplete"
                text={isSubmitting ? 'Uploading...' : 'Complete'}
                variant="primary"
                size="large"
                onClick={handleComplete}
                disabled={!canComplete}
              />
            </>
          )}
        </AgxSpacer>
      }
      setIsDialogOpen={handleDialogChange}
      isOpen={isOpen}
    />
  );
};

export default ContractUploadModal;
